import { useEffect, useMemo, useState } from 'react'
import { ACTIVE_TASK_STATUSES, TASK_STATUS } from '@ovrsee/shared'
import { Link, useSearchParams } from 'react-router'
import { EmptyState } from '../../components/EmptyState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { FormModal } from '../../components/FormModal.js'
import { LoadingState } from '../../components/LoadingState.js'
import { Topbar } from '../../components/Topbar.js'
import { FormError } from '../auth/FormError.js'
import { useCurrentPrompt } from '../innovation/useInnovation.js'
import { useAuth } from '../../lib/auth.js'
import { now } from '../../lib/time.js'
import { usePageTitle } from '../../lib/page-title.js'
import { TaskCard } from './TaskCard.js'
import { type MyTask, useBlockTask, useMyTasks, useStartTask, useSubmitTask } from './useMyTasks.js'
import { Button, Text, TextArea } from '../../ui/index.js'

type SubmitChannel = 'PLATFORM' | 'EXTERNAL'

function firstName(name: string): string {
  return name.split(/\s+/)[0] ?? name
}

function sortByUrgency(items: readonly MyTask[]): readonly MyTask[] {
  return [...items].sort((a, b) => Date.parse(a.effectiveDeadlineAt) - Date.parse(b.effectiveDeadlineAt))
}

function isDueToday(task: MyTask): boolean {
  const deadline = new Date(task.effectiveDeadlineAt)
  const today = now()
  return deadline.toDateString() === today.toDateString()
}

function SummaryMetric({ label, value }: { label: string; value: number }) {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        padding: 'var(--o-space-4)',
      }}
    >
      <div className="card-label">{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--fg)' }}>{value}</div>
    </div>
  )
}

function SubmitTaskModal({
  task,
  isOpen,
  onClose,
}: {
  task: MyTask | null
  isOpen: boolean
  onClose: () => void
}) {
  const submitTask = useSubmitTask()
  const [channel, setChannel] = useState<SubmitChannel>('PLATFORM')
  const [note, setNote] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setChannel('PLATFORM')
    setNote('')
    setFormError(null)
  }, [isOpen, task?.id])

  async function submit() {
    if (task === null) return
    setFormError(null)
    try {
      await submitTask.mutateAsync({
        taskId: task.id,
        channel,
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      })
      onClose()
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not submit that task.')
    }
  }

  return (
    <FormModal
      title="Submit task"
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Send for review"
      canSubmit={true}
      busy={submitTask.isPending}
    >
      <FormError message={formError} />

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label">Submission channel</label>
        <div role="group" aria-label="Submission channel" className="row-actions">
          <Button
            type="button"
            kind={channel === 'PLATFORM' ? 'primary' : 'secondary'}
            size="small"
            aria-pressed={channel === 'PLATFORM'}
            onClick={() => setChannel('PLATFORM')}
          >
            Through Ovrsee
          </Button>
          <Button
            type="button"
            kind={channel === 'EXTERNAL' ? 'primary' : 'secondary'}
            size="small"
            aria-pressed={channel === 'EXTERNAL'}
            onClick={() => setChannel('EXTERNAL')}
          >
            Completed elsewhere
          </Button>
        </div>
      </div>

      <div className="field">
        <label className="field-label" htmlFor="submit-note">
          Note
        </label>
        <TextArea
          id="submit-note"
          aria-label="Note"
          rows={4}
          value={note}
          onChange={setNote}
        />
      </div>
    </FormModal>
  )
}

function BlockTaskModal({
  task,
  isOpen,
  onClose,
}: {
  task: MyTask | null
  isOpen: boolean
  onClose: () => void
}) {
  const blockTask = useBlockTask()
  const [note, setNote] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setNote('')
    setFormError(null)
  }, [isOpen, task?.id])

  async function submit() {
    if (task === null) return
    setFormError(null)
    if (note.trim() === '') {
      setFormError('Tell your manager what is holding this up.')
      return
    }
    try {
      await blockTask.mutateAsync({ taskId: task.id, note: note.trim() })
      onClose()
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not flag that task as blocked.')
    }
  }

  return (
    <FormModal
      title="Flag task as blocked"
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Flag as blocked"
      canSubmit={true}
      busy={blockTask.isPending}
    >
      <FormError message={formError} />
      <div className="field">
        <label className="field-label" htmlFor="block-note">
          What is blocking this?
        </label>
        <TextArea
          id="block-note"
          aria-label="What is blocking this?"
          rows={4}
          value={note}
          onChange={setNote}
        />
      </div>
    </FormModal>
  )
}

export function MyDay() {
  usePageTitle('My Day')
  const { user } = useAuth()
  const [params] = useSearchParams()
  const linkedTaskId = params.get('task')
  const tasks = useMyTasks()
  const currentPrompt = useCurrentPrompt()
  const startTask = useStartTask()
  const [submitTask, setSubmitTask] = useState<MyTask | null>(null)
  const [blockTask, setBlockTask] = useState<MyTask | null>(null)

  const items = useMemo(() => sortByUrgency((tasks.data?.items ?? []) as readonly MyTask[]), [tasks.data?.items])

  const activeCount = items.filter((task) => ACTIVE_TASK_STATUSES.includes(task.status)).length
  const dueTodayCount = items.filter((task) => isDueToday(task)).length
  const overdueCount = items.filter((task) => task.isOverdue).length

  const first = user === null ? 'there' : firstName(user.name)

  return (
    <>
      <Topbar title="My day" eyebrow="Employee workspace" />

      <div className="page">
        <div className="page-section" style={{ marginBottom: 'var(--o-space-5)' }}>
          <div className="row-between" style={{ marginBottom: 'var(--o-space-4)', flexWrap: 'wrap' }}>
            <div>
              <div className="card-label">Today</div>
              <h2 style={{ margin: 0, fontSize: 24, color: 'var(--fg)' }}>Welcome back, {first}.</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 'var(--o-space-3)' }}>
              <SummaryMetric label="Active" value={activeCount} />
              <SummaryMetric label="Due today" value={dueTodayCount} />
              <SummaryMetric label="Overdue" value={overdueCount} />
            </div>
          </div>
          <Text color="secondary">Focus on the next step. Your manager sees progress only.</Text>
        </div>

        {currentPrompt.data?.prompt != null ? (
          <div className="page-section" style={{ marginBottom: 'var(--o-space-5)', fontSize: 13, color: 'var(--fg-2)' }}>
            This period&rsquo;s challenge: {currentPrompt.data.prompt.question}{' '}
            <Link to="/innovation">Share an idea</Link>
          </div>
        ) : null}

        {tasks.isPending ? <LoadingState label="Loading your day" /> : null}
        {tasks.isError ? <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} /> : null}

        {tasks.isSuccess ? (
          items.length === 0 ? (
            <EmptyState title="Nothing on your plate right now." />
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 'var(--o-space-4)',
              }}
            >
              {items.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  highlight={task.id === linkedTaskId}
                  onStart={() => void startTask.mutate(task.id)}
                  onSubmit={() => setSubmitTask(task)}
                  onBlock={() => setBlockTask(task)}
                />
              ))}
            </div>
          )
        ) : null}
      </div>

      <SubmitTaskModal task={submitTask} isOpen={submitTask !== null} onClose={() => setSubmitTask(null)} />
      <BlockTaskModal task={blockTask} isOpen={blockTask !== null} onClose={() => setBlockTask(null)} />
    </>
  )
}
